import { isNurse } from '@/data/service/authService';
import { Logger } from '@montu-web/utilities';

import { Button, Dialog, DialogContent, DialogTitle, Typography } from '@montugroup/design-system';
import { CircularProgress } from '@mui/material';
import type { ShowFnOutput } from 'mui-modal-provider';
import { useModal } from 'mui-modal-provider';

interface UseAutoSpotConsultationCreationDialogProps {
  onCreateSuccess: () => Promise<void>;
  hasError: boolean;
}

const logger = new Logger('useAutoSpotConsultationCreationDialog');

const ErrorDialog = ({ onClose, ...rest }: { open: boolean; onClose: () => void }) => {
  return (
    <Dialog {...rest}>
      <DialogTitle>Unable to create spot consult</DialogTitle>
      <DialogContent sx={{ pb: 6, textAlign: 'center' }}>
        <Typography variant="subtitle1" color="primary" sx={{ mb: 3 }}>
          You can manually create a spot consult by clicking &quot;Arrange spot consult&quot; in PMS
        </Typography>
        <Button
          variant="contained"
          sx={{ margin: '0 auto', position: 'relative', borderRadius: '5px', display: 'block' }}
          onClick={onClose}
        >
          Close
        </Button>
      </DialogContent>
    </Dialog>
  );
};

const LoadingDialog = (props: { open: boolean }) => {
  return (
    <Dialog {...props}>
      <DialogTitle>Creating spot consults</DialogTitle>
      <DialogContent>
        <CircularProgress size={'1.5rem'} sx={{ margin: '0 auto', position: 'relative', display: 'block' }} />
      </DialogContent>
    </Dialog>
  );
};

export const useAutoSpotConsultationCreationDialog = ({
  onCreateSuccess,
  hasError,
}: UseAutoSpotConsultationCreationDialogProps) => {
  const { showModal } = useModal();
  let errorModal: ShowFnOutput<typeof ErrorDialog> | undefined;

  const onModalClose = () => {
    if (errorModal) {
      errorModal.hide();
    }
  };

  const showDialog = async () => {
    if (!isNurse()) {
      return;
    }
    if (!hasError) {
      const modal = showModal(LoadingDialog, undefined, { hideOnClose: false });
      try {
        await onCreateSuccess();
        modal.hide();
      } catch (error) {
        modal.hide();
        logger.error('Failed to create spot consult', error);
        // Show error modal
        errorModal = showModal(ErrorDialog, { onClose: onModalClose }, { hideOnClose: false });
      }
    } else {
      errorModal = showModal(ErrorDialog, { onClose: onModalClose }, { hideOnClose: false });
    }
  };

  return { showDialog };
};
